import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { finalize } from 'rxjs';
import {
  readVaultItemsPerPageFromPreferences,
  VaultAppToastService,
  VAULT_TABLE_PAGE_SIZE_OPTIONS,
  VAULT_TABLE_PAGINATION_LABELS,
} from '@nebula/shared-vault/data-access';
import {
  patchVaultModuleInitialLoadError,
  toModuleAccessErrorMessage,
  type PageHeaderAction,
} from '@nebula-shared/ui';
import { type ActionEvent, type SortChangeEvent, type TableConfig } from 'nebula-ui-kit';
import { DocumentRetentionService } from '../../../data-access/src/lib/retention/document-retention.service';
import { RetentionPolicyService } from '../../../data-access/src/lib/retention/retention-policy.service';
import {
  type DocumentRetention,
  type ExpiringDocument,
  type RetentionDispositionAction,
  type RetentionDocumentType,
  type RetentionPolicy,
  type RetentionStatus,
  type RetentionUnit,
} from '../../../domain/src/lib/retention/retention.model';
import {
  parseRetentionDateForPicker,
  toRetentionDispositionLabel,
  toRetentionDocumentTypeLabel,
  toRetentionPolicyStartDateApi,
  toRetentionStatusLabel,
  toRetentionStatusVariant,
} from '../../../feature-vault/src/lib/retention/retention-view.utils';
import {
  buildRetentionPolicyCreatedToast,
  buildRetentionPolicyUpdatedToast,
  buildVaultSuccessToast,
} from '../../../feature-vault/src/lib/vault-module-feedback.util';
import { loadPersistedState, savePersistedState } from './signal-store-persist.util';

export interface RetentionPolicyRow extends RetentionPolicy {
  activeLabel: string;
  retentionLabel: string;
  documentTypeLabel: string;
  dispositionLabel: string;
}

export interface RetentionDocumentRow extends DocumentRetention {
  stateLabel: string;
  stateVariant: 'info' | 'warning' | 'error' | 'success' | 'neutral';
  startsOnLabel: string;
  endsOnLabel: string;
  suspendedLabel: string;
  documentTypeLabel: string;
}

export interface RetentionPolicyForm {
  name: string;
  description: string;
  document_type: RetentionDocumentType;
  retention_period: number;
  retention_unit: RetentionUnit;
  disposition_action: RetentionDispositionAction;
  notify_days_before: number;
  is_active: boolean;
  retention_start_date: Date | null;
}

type RetentionSection = 'policies' | 'documents' | 'expiring';
export type PendingRetentionAction =
  | { id: string; action: 'suspend' | 'resume' }
  | { id: string; action: 'activate' | 'deactivate'; name: string }
  | { id: string; action: 'delete'; name: string };

interface RetentionStoreState {
  loading: boolean;
  saving: boolean;
  accessDenied: boolean;
  loadError: string | null;
  loadErrorTitle: string | null;
  error: string | null;
  info: string | null;
  activeSection: RetentionSection;
  searchTerm: string;
  retentionSearchTerm: string;
  retentionStatusFilter: 'all' | RetentionStatus;
  sortColumn: keyof RetentionPolicyRow | null;
  sortDirection: 'asc' | 'desc';
  policies: RetentionPolicyRow[];
  retentions: RetentionDocumentRow[];
  expiringDocs: ExpiringDocument[];
  editingId: string | null;
  /** Alta oculta hasta pulsar «Nueva política» (mismo patrón que expediente / custodia). */
  creating: boolean;
  createForm: RetentionPolicyForm;
  editForm: RetentionPolicyForm;
  createFormResetKey: number;
  createNameError: string | null;
  editNameError: string | null;
  selectedPolicy: RetentionPolicyRow | null;
}

type RetentionStorePersistedState = Pick<
  RetentionStoreState,
  | 'activeSection'
  | 'searchTerm'
  | 'retentionSearchTerm'
  | 'retentionStatusFilter'
  | 'sortColumn'
  | 'sortDirection'
>;

const STORAGE_KEY = 'vault_retention_state';
const STORAGE_VERSION = 1;
const persistConfig = {
  key: STORAGE_KEY,
  version: STORAGE_VERSION,
  storage: 'session' as const,
};

const initialState: RetentionStoreState = {
  loading: false,
  saving: false,
  accessDenied: false,
  loadError: null,
  loadErrorTitle: null,
  error: null,
  info: null,
  activeSection: 'policies',
  searchTerm: '',
  retentionSearchTerm: '',
  retentionStatusFilter: 'all',
  sortColumn: 'name',
  sortDirection: 'asc',
  policies: [],
  retentions: [],
  expiringDocs: [],
  editingId: null,
  creating: false,
  createForm: emptyForm(),
  editForm: emptyForm(),
  createFormResetKey: 0,
  createNameError: null,
  editNameError: null,
  selectedPolicy: null,
};

const documentTypeOptions: Array<{ label: string; value: RetentionDocumentType }> = [
  { label: 'Documentos de Ingesta', value: 'ingest' },
];

const unitOptions: Array<{ label: string; value: RetentionUnit }> = [
  { label: 'Días', value: 'days' },
  { label: 'Meses', value: 'months' },
  { label: 'Años', value: 'years' },
];

const actionOptions: Array<{ label: string; value: RetentionDispositionAction }> = [
  { label: 'Eliminar', value: 'delete' },
  { label: 'Archivar', value: 'archive' },
  { label: 'Revisión', value: 'review' },
];

const retentionStatusOptions: Array<{ label: string; value: 'all' | RetentionStatus }> = [
  { label: 'Todas', value: 'all' },
  { label: 'Activas', value: 'active' },
  { label: 'Expiradas', value: 'expired' },
  { label: 'Suspendidas', value: 'suspended' },
  { label: 'Dispuestas', value: 'disposed' },
];

const tableConfig: TableConfig<RetentionPolicyRow> = {
  columns: [
    { key: 'name', label: 'Política', sortable: true, align: 'left' },
    { key: 'documentTypeLabel', label: 'Tipo', sortable: true, align: 'center', width: '150px' },
    { key: 'retentionLabel', label: 'Retención', sortable: false, align: 'center', width: '160px' },
    {
      key: 'dispositionLabel',
      label: 'Disposición',
      sortable: true,
      align: 'center',
      width: '130px',
    },
    {
      key: 'activeLabel',
      label: 'Estado',
      sortable: true,
      align: 'center',
      width: '110px',
      cellTemplate: true,
    },
    {
      key: '_actions',
      label: 'Acciones',
      sortable: false,
      align: 'center',
      width: '10.5rem',
      cellTemplate: true,
    },
  ],
  showPagination: true,
  rowClickable: true,
  defaultAction: 'view',
  pageSize: readVaultItemsPerPageFromPreferences(),
  pageSizeOptions: [...VAULT_TABLE_PAGE_SIZE_OPTIONS],
  paginationLabels: { ...VAULT_TABLE_PAGINATION_LABELS },
  ariaLabel: 'Políticas de retención documental',
  initialSort: { column: 'name', direction: 'asc' },
};

const documentsTableConfig: TableConfig<RetentionDocumentRow> = {
  columns: [
    { key: 'document_id', label: 'Documento', sortable: false, align: 'left' },
    { key: 'documentTypeLabel', label: 'Tipo', sortable: false, align: 'center', width: '120px' },
    { key: 'startsOnLabel', label: 'Inicio', sortable: false, align: 'center', width: '130px' },
    { key: 'endsOnLabel', label: 'Vence', sortable: false, align: 'center', width: '130px' },
    {
      key: 'stateLabel',
      label: 'Estado',
      sortable: false,
      align: 'center',
      width: '140px',
      cellTemplate: true,
    },
    { key: 'suspendedLabel', label: 'Suspensión', sortable: false, align: 'left' },
  ],
  actions: [
    {
      label: 'Suspender',
      icon: 'pause',
      action: 'suspend',
      color: 'warn',
      visible: (row) => row.status === 'active' && !row.is_suspended,
    },
    {
      label: 'Reanudar',
      icon: 'play',
      action: 'resume',
      visible: (row) => row.is_suspended,
    },
  ],
  showPagination: true,
  rowClickable: false,
  pageSize: readVaultItemsPerPageFromPreferences(),
  pageSizeOptions: [...VAULT_TABLE_PAGE_SIZE_OPTIONS],
  paginationLabels: { ...VAULT_TABLE_PAGINATION_LABELS },
  ariaLabel: 'Documentos bajo retención',
};

function isRetentionPersistedState(value: unknown): value is RetentionStorePersistedState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  const sectionOk =
    v['activeSection'] === 'policies' ||
    v['activeSection'] === 'documents' ||
    v['activeSection'] === 'expiring';
  const statusOk =
    v['retentionStatusFilter'] === 'all' ||
    v['retentionStatusFilter'] === 'active' ||
    v['retentionStatusFilter'] === 'expired' ||
    v['retentionStatusFilter'] === 'disposed' ||
    v['retentionStatusFilter'] === 'suspended';
  const sortDirectionOk = v['sortDirection'] === 'asc' || v['sortDirection'] === 'desc';
  const sortColumnOk = typeof v['sortColumn'] === 'string' || v['sortColumn'] === null;
  return (
    sectionOk &&
    statusOk &&
    sortDirectionOk &&
    sortColumnOk &&
    typeof v['searchTerm'] === 'string' &&
    typeof v['retentionSearchTerm'] === 'string'
  );
}

const persistedInitialState: RetentionStoreState = {
  ...initialState,
  ...(loadPersistedState(persistConfig, isRetentionPersistedState) ?? {}),
};

type RetentionStorePersistenceSource = {
  activeSection: () => RetentionSection;
  searchTerm: () => string;
  retentionSearchTerm: () => string;
  retentionStatusFilter: () => 'all' | RetentionStatus;
  sortColumn: () => keyof RetentionPolicyRow | null;
  sortDirection: () => 'asc' | 'desc';
};

function persistState(state: RetentionStorePersistenceSource): void {
  const persisted: RetentionStorePersistedState = {
    activeSection: state.activeSection(),
    searchTerm: state.searchTerm(),
    retentionSearchTerm: state.retentionSearchTerm(),
    retentionStatusFilter: state.retentionStatusFilter(),
    sortColumn: state.sortColumn(),
    sortDirection: state.sortDirection(),
  };
  savePersistedState(persistConfig, persisted);
}

export const retentionStore = signalStore(
  { providedIn: 'root' },
  withState(persistedInitialState),
  withComputed((state) => ({
    documentTypeOptions: computed(() => documentTypeOptions),
    unitOptions: computed(() => unitOptions),
    actionOptions: computed(() => actionOptions),
    retentionStatusOptions: computed(() => retentionStatusOptions),
    tableConfig: computed(() => tableConfig),
    documentsTableConfig: computed(() => documentsTableConfig),
    headerActions: computed<PageHeaderAction[]>(() => {
      const actions: PageHeaderAction[] = [];
      if (state.activeSection() === 'policies') {
        actions.push({
          id: 'new',
          label: 'Nueva política',
          icon: 'Plus',
          variant: 'primary',
          disabled: state.loading() || state.saving(),
        });
      }
      actions.push({
        id: 'reload',
        label: 'Actualizar',
        icon: 'RefreshCcw',
        variant: 'secondary',
        loading: state.loading(),
        disabled: state.loading(),
      });
      return actions;
    }),
    filteredPolicies: computed(() => {
      const term = state.searchTerm().trim().toLowerCase();
      const list = [...state.policies()];
      const filtered = !term
        ? list
        : list.filter((p) =>
            [p.name, p.description ?? '', p.document_type, p.disposition_action]
              .join(' ')
              .toLowerCase()
              .includes(term),
          );
      const col = state.sortColumn();
      const dir = state.sortDirection();
      if (!col) return filtered;
      return filtered.sort((a, b) => {
        const av = String(a[col] ?? '').toLowerCase();
        const bv = String(b[col] ?? '').toLowerCase();
        const cmp = av.localeCompare(bv);
        return dir === 'asc' ? cmp : -cmp;
      });
    }),
    filteredRetentions: computed(() => {
      const term = state.retentionSearchTerm().trim().toLowerCase();
      const status = state.retentionStatusFilter();
      return state.retentions().filter((row) => {
        const matchesTerm =
          !term ||
          [row.document_id, row.policy_id, row.document_type, row.status]
            .join(' ')
            .toLowerCase()
            .includes(term);
        const matchesStatus = status === 'all' ? true : row.status === status;
        return matchesTerm && matchesStatus;
      });
    }),
  })),
  /* eslint-disable max-lines-per-function -- SignalStore withMethods agrupa todos los métodos del store */
  withMethods(
    (
      store,
      retentionPolicyService = inject(RetentionPolicyService),
      documentRetentionService = inject(DocumentRetentionService),
      vaultAppToast = inject(VaultAppToastService),
    ) => ({
      init(): void {
        this.loadPolicies();
        this.loadRetentionDocuments();
        this.loadExpiringDocuments();
      },
      clearPageError(): void {
        patchState(store, { error: null });
      },
      clearPageInfo(): void {
        patchState(store, { info: null });
      },
      onHeaderAction(actionId: string): void {
        if (actionId === 'new') {
          this.openCreatePanel();
          return;
        }
        if (actionId === 'reload') {
          this.loadPolicies();
          this.loadRetentionDocuments();
          this.loadExpiringDocuments();
        }
      },
      openCreatePanel(): void {
        patchState(store, {
          creating: true,
          editingId: null,
          selectedPolicy: null,
          createNameError: null,
          createForm: emptyForm(),
          createFormResetKey: store.createFormResetKey() + 1,
          error: null,
        });
      },
      closeCreatePanel(): void {
        patchState(store, {
          creating: false,
          createForm: emptyForm(),
          createFormResetKey: store.createFormResetKey() + 1,
          createNameError: null,
        });
      },
      onSearchTermChange(value: string): void {
        patchState(store, { searchTerm: value ?? '' });
        persistState(store);
      },
      onSortChange(ev: SortChangeEvent<RetentionPolicyRow>): void {
        patchState(store, {
          sortColumn: (ev.column as keyof RetentionPolicyRow) ?? null,
          sortDirection: ev.direction || 'asc',
        });
        persistState(store);
      },
      setActiveSection(section: RetentionSection): void {
        patchState(store, {
          activeSection: section,
          ...(section !== 'policies'
            ? {
                creating: false,
                createForm: emptyForm(),
                createNameError: null,
              }
            : {}),
        });
        persistState(store);
      },
      onRetentionSearchTermChange(value: string): void {
        patchState(store, { retentionSearchTerm: value ?? '' });
        persistState(store);
      },
      onRetentionStatusFilterChange(value: string): void {
        const filter =
          value === 'active' || value === 'expired' || value === 'disposed' || value === 'suspended'
            ? value
            : 'all';
        patchState(store, { retentionStatusFilter: filter });
        persistState(store);
        this.loadRetentionDocuments();
      },
      onCreateField<K extends keyof RetentionPolicyForm>(
        key: K,
        value: RetentionPolicyForm[K],
      ): void {
        patchState(store, {
          createForm: { ...store.createForm(), [key]: value },
          ...(key === 'name' ? { createNameError: null } : {}),
        });
      },
      onEditField<K extends keyof RetentionPolicyForm>(
        key: K,
        value: RetentionPolicyForm[K],
      ): void {
        patchState(store, {
          editForm: { ...store.editForm(), [key]: value },
          ...(key === 'name' ? { editNameError: null } : {}),
        });
      },
      createPolicy(): void {
        const form = store.createForm();
        if (!form.name.trim()) {
          patchState(store, { createNameError: 'El nombre de la política es obligatorio.' });
          return;
        }
        if (!Number.isFinite(form.retention_period) || form.retention_period <= 0) {
          patchState(store, { error: 'El periodo de retención debe ser mayor a cero.' });
          return;
        }
        const policyName = form.name.trim();
        patchState(store, { error: null, info: null, createNameError: null, saving: true });
        const notify = Number(form.notify_days_before);
        const payload: Partial<RetentionPolicy> = {
          name: policyName,
          description: form.description?.trim() || undefined,
          document_type: form.document_type,
          retention_period: Number(form.retention_period),
          retention_unit: form.retention_unit,
          disposition_action: form.disposition_action,
          is_active: form.is_active,
          retention_start_date: toRetentionPolicyStartDateApi(form.retention_start_date),
          /** OpenAPI `RetentionPolicyCreate`: entero `>= 0` (default 30 en servidor si se omite). */
          notify_days_before: Number.isFinite(notify) && notify >= 0 ? notify : 30,
        };
        retentionPolicyService
          .createPolicy(payload)
          .pipe(finalize(() => patchState(store, { saving: false })))
          .subscribe({
            next: () => {
              patchState(store, {
                creating: false,
                createForm: emptyForm(),
                createFormResetKey: store.createFormResetKey() + 1,
                createNameError: null,
                error: null,
                info: null,
              });
              vaultAppToast.show(buildRetentionPolicyCreatedToast(policyName));
              this.loadPolicies();
            },
            error: (err: unknown) => {
              patchState(store, {
                error: toModuleAccessErrorMessage(err, {
                  moduleLabel: 'Retención Documental',
                  fallback: 'No se pudo crear la política.',
                }),
              });
            },
          });
      },
      onTableAction(ev: ActionEvent<RetentionPolicyRow>): void {
        const row = ev?.data;
        if (!row) return;
        if (ev.action === 'view') {
          this.openPolicyDetail(row);
          return;
        }
        if (ev.action === 'edit') {
          this.startEdit(row);
        }
      },
      openPolicyDetail(row: RetentionPolicyRow): void {
        patchState(store, { selectedPolicy: row, error: null });
        retentionPolicyService.getPolicy(row.id).subscribe({
          next: (detail) => patchState(store, { selectedPolicy: toRow(detail) }),
          error: () => {
            /* Mantener datos de la fila si el detalle no responde. */
          },
        });
      },
      closePolicyDetail(): void {
        patchState(store, { selectedPolicy: null });
      },
      onDocumentTableAction(_ev: ActionEvent<RetentionDocumentRow>): void {
        // Las confirmaciones se gestionan en el componente con el diálogo estándar del UI Kit.
      },
      executeRetentionAction(pending: PendingRetentionAction, reason?: string): void {
        if (
          pending.action === 'activate' ||
          pending.action === 'deactivate' ||
          pending.action === 'delete'
        ) {
          patchState(store, { saving: true });
          const request$ =
            pending.action === 'delete'
              ? retentionPolicyService.deletePolicy(pending.id)
              : retentionPolicyService.updatePolicy(pending.id, {
                  is_active: pending.action === 'activate',
                });
          request$.pipe(finalize(() => patchState(store, { saving: false }))).subscribe({
            next: () => {
              const toast =
                pending.action === 'activate'
                  ? buildVaultSuccessToast(
                      'Política activada',
                      'La política quedó activa y aplicará según su configuración.',
                    )
                  : pending.action === 'deactivate'
                    ? buildVaultSuccessToast(
                        'Política desactivada',
                        'La política quedó inactiva hasta que se reactive.',
                      )
                    : buildVaultSuccessToast(
                        'Política eliminada',
                        'La política se eliminó correctamente.',
                      );
              vaultAppToast.show(toast);
              patchState(store, {
                info: null,
                editingId: store.editingId() === pending.id ? null : store.editingId(),
              });
              this.loadPolicies();
            },
            error: (err: unknown) => {
              patchState(store, {
                error: toModuleAccessErrorMessage(err, {
                  moduleLabel: 'Retención Documental',
                  fallback:
                    pending.action === 'delete'
                      ? 'No se pudo eliminar la política.'
                      : 'No se pudo cambiar el estado.',
                }),
              });
            },
          });
          return;
        }

        patchState(store, { saving: true });
        const request$ =
          pending.action === 'suspend'
            ? documentRetentionService.suspendRetention(pending.id, reason?.trim() || undefined)
            : documentRetentionService.resumeRetention(pending.id);
        request$.pipe(finalize(() => patchState(store, { saving: false }))).subscribe({
          next: () => {
            vaultAppToast.show(
              buildVaultSuccessToast(
                pending.action === 'suspend' ? 'Retención suspendida' : 'Retención reanudada',
                pending.action === 'suspend'
                  ? 'La retención del documento quedó suspendida.'
                  : 'La retención del documento se reanudó.',
              ),
            );
            patchState(store, { info: null });
            this.loadRetentionDocuments();
            this.loadExpiringDocuments();
          },
          error: (err: unknown) => {
            patchState(store, {
              error: toModuleAccessErrorMessage(err, {
                moduleLabel: 'Retención Documental',
                fallback: 'No se pudo ejecutar la acción de retención.',
              }),
            });
          },
        });
      },
      startEdit(row: RetentionPolicyRow): void {
        patchState(store, {
          creating: false,
          createForm: emptyForm(),
          createNameError: null,
          editingId: row.id,
          selectedPolicy: null,
          editNameError: null,
          editForm: {
            name: row.name || '',
            description: row.description || '',
            document_type: row.document_type,
            retention_period: Number(row.retention_period || 0),
            retention_unit: row.retention_unit,
            disposition_action: row.disposition_action,
            notify_days_before: Number(row.notify_days_before || 0),
            is_active: !!row.is_active,
            retention_start_date: parseRetentionDateForPicker(row.retention_start_date),
          },
        });
      },
      saveEdit(): void {
        const id = store.editingId();
        if (!id) return;
        const form = store.editForm();
        if (!form.name.trim()) {
          patchState(store, { editNameError: 'El nombre de la política es obligatorio.' });
          return;
        }
        if (!Number.isFinite(form.retention_period) || form.retention_period <= 0) {
          patchState(store, { error: 'El periodo de retención debe ser mayor a cero.' });
          return;
        }
        patchState(store, { error: null, info: null, editNameError: null, saving: true });
        const policyName = form.name.trim();
        const notifyEdit = Number(form.notify_days_before);
        retentionPolicyService
          .updatePolicy(id, {
            name: policyName,
            description: form.description?.trim() || '',
            document_type: form.document_type,
            retention_period: Number(form.retention_period),
            retention_unit: form.retention_unit,
            disposition_action: form.disposition_action,
            is_active: form.is_active,
            retention_start_date: toRetentionPolicyStartDateApi(form.retention_start_date),
            notify_days_before: Number.isFinite(notifyEdit) && notifyEdit >= 0 ? notifyEdit : 30,
          })
          .pipe(finalize(() => patchState(store, { saving: false })))
          .subscribe({
            next: () => {
              vaultAppToast.show(buildRetentionPolicyUpdatedToast(policyName));
              patchState(store, { editingId: null, info: null });
              this.loadPolicies();
            },
            error: (err: unknown) => {
              patchState(store, {
                error: toModuleAccessErrorMessage(err, {
                  moduleLabel: 'Retención Documental',
                  fallback: 'No se pudo actualizar la política.',
                }),
              });
            },
          });
      },
      cancelEdit(): void {
        patchState(store, { editingId: null, editForm: emptyForm(), editNameError: null });
      },
      loadRetentionDocuments(): void {
        documentRetentionService
          .listRetentions(
            store.retentionStatusFilter() === 'all' ? undefined : store.retentionStatusFilter(),
          )
          .subscribe({
            next: (rows: DocumentRetention[]) => {
              patchState(store, { retentions: (rows ?? []).map((row) => toDocumentRow(row)) });
            },
            error: (err: unknown) => {
              patchState(store, {
                error: toModuleAccessErrorMessage(err, {
                  moduleLabel: 'Retención Documental',
                  fallback: 'No se pudieron cargar los documentos bajo retención.',
                }),
                retentions: [],
              });
            },
          });
      },
      loadExpiringDocuments(): void {
        documentRetentionService.getExpiringDocuments(30).subscribe({
          next: (rows: ExpiringDocument[]) => patchState(store, { expiringDocs: rows ?? [] }),
          error: () => patchState(store, { expiringDocs: [] }),
        });
      },
      loadPolicies(): void {
        patchState(store, {
          error: null,
          loadError: null,
          loadErrorTitle: null,
          accessDenied: false,
          loading: true,
        });
        retentionPolicyService
          .listPolicies()
          .pipe(finalize(() => patchState(store, { loading: false })))
          .subscribe({
            next: (rows: RetentionPolicy[]) =>
              patchState(store, { policies: (rows ?? []).map((r) => toRow(r)) }),
            error: (err: unknown) => {
              patchState(store, {
                ...patchVaultModuleInitialLoadError(err, {
                  moduleLabel: 'Retención Documental',
                  fallback: 'No se pudieron cargar las políticas.',
                }),
                policies: [],
              });
            },
          });
      },
      toInputValue(value: number): string {
        return Number.isFinite(value) ? String(value) : '';
      },
    }),
  ),
);

function toRow(policy: RetentionPolicy): RetentionPolicyRow {
  const unitLabel =
    policy.retention_unit === 'days'
      ? 'día(s)'
      : policy.retention_unit === 'months'
        ? 'mes(es)'
        : 'año(s)';
  return {
    ...policy,
    activeLabel: policy.is_active ? 'Activa' : 'Inactiva',
    retentionLabel: `${policy.retention_period} ${unitLabel}`,
    documentTypeLabel: toRetentionDocumentTypeLabel(policy.document_type),
    dispositionLabel: toRetentionDispositionLabel(policy.disposition_action),
  };
}

function toDocumentRow(row: DocumentRetention): RetentionDocumentRow {
  return {
    ...row,
    stateLabel: toRetentionStatusLabel(row.status),
    stateVariant: toRetentionStatusVariant(row.status),
    startsOnLabel: toShortDate(row.retention_start_date),
    endsOnLabel: toShortDate(row.retention_end_date),
    suspendedLabel: row.is_suspended ? row.suspension_reason || 'Suspendida manualmente' : '-',
    documentTypeLabel: toRetentionDocumentTypeLabel(row.document_type),
  };
}

function toShortDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('es-CO');
}

function emptyForm(): RetentionPolicyForm {
  return {
    name: '',
    description: '',
    document_type: 'ingest',
    retention_period: 1,
    retention_unit: 'years',
    disposition_action: 'review',
    notify_days_before: 30,
    is_active: true,
    retention_start_date: null,
  };
}

export type RetentionStore = typeof retentionStore;
